import { SxProps } from '@mui/material';
import { useField } from 'formik';
import React from 'react';
import { Autocomplete } from 'src/components/Autocomplete';
import { HtmlTypography } from 'src/components/HtmlTypography';
import { ListItem } from 'src/components/ListItem';
import { TextField } from 'src/components/TextField';
import { VirtualListbox } from 'src/components/VirtualListbox';
import { Identifier } from 'src/types/utilityTypes';
import { isEmpty } from 'src/utils/typeCheck';

export interface BaseSelectOption {
  id: Identifier;
  label: string;
  value?: string | object;
  disabled?: boolean;
  secondary?: string;
  type?: string;
}

export interface BaseSelectProps {
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
  loading?: boolean;
  options: BaseSelectOption[];
  multiple?: boolean;
  value: Identifier | Identifier[] | null;
  onChange: {
    (value: BaseSelectOption | Identifier | null): void;
    (value: BaseSelectOption[] | Identifier[]): void;
  };
  valueOnly?: boolean;
  blurOnSelect?: boolean;
  sx?: SxProps;
  virtualized?: boolean;
  filterOptions?: (
    options: BaseSelectOption[],
    { inputValue }: { inputValue: string }
  ) => BaseSelectOption[];
  groupBy?: (option: BaseSelectOption) => string;
}

export type SelectProps = BaseSelectProps;

export interface FormSelectProps
  extends Omit<BaseSelectProps, 'error' | 'options' | 'value' | 'onChange'> {
  name: string;
}

export const BaseSelect = ({
  value,
  onChange,
  label,
  placeholder = 'Select...',
  disabled = false,
  error,
  helperText,
  loading = false,
  options,
  multiple = false,
  valueOnly = true,
  blurOnSelect = false,
  virtualized = false,
  groupBy,
  sx,
  filterOptions,
}: BaseSelectProps) => {
  if (multiple) {
    // Preserve the order of selection by mapping value array to options
    const selectedOptions = Array.isArray(value)
      ? value
          .map((id) => options.find((option) => option.id === id))
          .filter((option): option is BaseSelectOption => option !== undefined)
      : [];

    return (
      <Autocomplete
        multiple
        groupBy={groupBy}
        options={options}
        getOptionLabel={(option) => option.label}
        value={selectedOptions}
        onChange={(_, newValue) => {
          onChange(valueOnly ? newValue.map((v) => v.id) : newValue);
        }}
        disableListWrap={virtualized}
        slotProps={{
          listbox: {
            component: virtualized ? VirtualListbox : undefined,
          },
        }}
        loading={loading}
        filterOptions={filterOptions || undefined}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            disabled={disabled || loading}
          />
        )}
        renderOption={(props, option) => (
          <>
            {!option.disabled ? (
              <ListItem
                {...(props as any)}
                clickable={!option.disabled}
                primary={option.label}
                secondary={option.secondary}
              />
            ) : (
              <ListItem
                primary={option.label}
                secondary={option.secondary}
                sx={{ pl: 1, color: 'text.secondary' }}
              />
            )}
          </>
        )}
        sx={sx}
      />
    );
  }

  return (
    <Autocomplete
      groupBy={groupBy}
      blurOnSelect={blurOnSelect}
      options={options}
      getOptionLabel={(option) => option.label}
      value={options.find((option) => option.id === value) || null}
      disableListWrap={virtualized}
      slotProps={{
        listbox: {
          component: virtualized ? VirtualListbox : undefined,
        },
      }}
      onChange={(_, newValue) => {
        onChange(
          valueOnly ? (isEmpty(newValue?.id) ? null : newValue?.id) : newValue
        );
      }}
      loading={loading}
      filterOptions={filterOptions || undefined}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error}
          helperText={helperText}
          disabled={disabled || loading}
        />
      )}
      renderOption={(props, option) => (
        <>
          {!option.disabled ? (
            <ListItem
              {...(props as any)}
              clickable={!option.disabled}
              primary={<HtmlTypography>{option.label}</HtmlTypography>}
              secondary={option.secondary}
            />
          ) : (
            <ListItem
              primary={<HtmlTypography>{option.label}</HtmlTypography>}
              secondary={option.secondary}
              sx={{ pl: 1, color: 'text.secondary' }}
            />
          )}
        </>
      )}
      sx={sx}
    />
  );
};

export const createFormSelect = <T extends FormSelectProps>(
  SelectComponent: React.ComponentType<
    Omit<BaseSelectProps, 'options' | 'loading'>
  >
) => {
  return function FormSelect({
    name,
    label,
    placeholder,
    disabled,
    multiple,
    ...props
  }: T) {
    const [field, meta] = useField({ name });

    return (
      <SelectComponent
        value={
          multiple
            ? field.value || []
            : isEmpty(field.value)
            ? null
            : field.value
        }
        onChange={(value) => {
          field.onChange({ target: { name, value } });
        }}
        label={label}
        placeholder={placeholder}
        disabled={disabled}
        error={!!(meta.touched && meta.error)}
        helperText={meta.touched ? meta.error : undefined}
        multiple={multiple}
        {...props}
      />
    );
  };
};
